/**
 * ChildGoalsTable — goals inherited from child TARAs in hierarchical
 * mode. Read-only data + Relevant checkbox + Justification input.
 * Mirror of step5-attack-paths/ChildAttackPathsTable.tsx.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import FilterDrawer from '../../../../components/ui/DataTable/FilterDrawer';
import LevelBadge from '../../../../components/ui/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import { getCALColor } from '../../../../utils/severityStyle';
import { SECURITY_PROPERTIES, type GoalRecord } from './goalsFieldSchema';
import styles from './Step7Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const FilterDrawerAny = FilterDrawer as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

const renderProperty = (row: ChildGoalView) =>
  row.security_property ? <CategoryPill label={String(row.security_property)} size="sm" /> : '';

const renderCal = (row: ChildGoalView) =>
  row.cal_level ? (
    <LevelBadge color={getCALColor(row.cal_level)} label={String(row.cal_level)} size="sm" />
  ) : (
    ''
  );

export interface ChildGoalView extends GoalRecord {}

export interface ChildGoalsTableProps {
  goals: ChildGoalView[];
  onToggleRelevance: (goalId: string) => void;
  onJustificationChange: (goalId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildGoalsTable({
  goals,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildGoalsTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [propertyValues, setPropertyValues] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const propertyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of SECURITY_PROPERTIES) counts[p.value] = 0;
    for (const g of goals) {
      const k = String(g.security_property ?? '');
      if (k in counts) counts[k] += 1;
    }
    return counts;
  }, [goals]);

  const propertyFiltered = useMemo(
    () => (propertyValues.length === 0
      ? goals
      : goals.filter((g) => propertyValues.includes(String(g.security_property ?? '')))),
    [goals, propertyValues],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return propertyFiltered;
    return propertyFiltered.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [propertyFiltered, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.goal_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'goal_id', label: 'ID', sortable: true, width: '7%' },
      { key: 'goal_statement', label: 'Goal Statement', sortable: false, width: '35%' },
      {
        key: 'risk_ids',
        label: 'Linked Risks',
        sortable: false,
        width: '12%',
        render: (row: ChildGoalView) => joinList(row.risk_ids),
      },
      { key: 'security_property', label: 'Property', sortable: true, width: '10%', render: renderProperty },
      { key: 'cal_level', label: 'CAL', sortable: true, width: '7%', render: renderCal },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '10%',
        render: (row: ChildGoalView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.goal_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.goal_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '19%',
        render: (row: ChildGoalView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.goal_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  const propertyFilter = {
    key: 'security_property',
    label: 'Property',
    values: propertyValues,
    onChange: (next: string[]) => { setPropertyValues(next); setPage(1); },
    options: SECURITY_PROPERTIES.map((p) => ({
      value: p.value,
      label: p.label,
      count: propertyCounts[p.value] ?? 0,
    })),
  };

  return (
    <div className={styles.tableShell}>
      <FilterDrawerAny
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={[propertyFilter]}
      />
      <DataTableAny
        columns={columns}
        data={keyed}
        rowKey="_rowKey"
        emptyMessage="No inherited goals from child TARAs."
        toolbar={
          <TableToolbarAny
            search={{
              value: search,
              onChange: (v: string) => { setSearch(v); setPage(1); },
              placeholder: 'Search inherited goals…',
            }}
            count={{ value: filtered.length, label: 'Inherited Goals' }}
            onFilterToggle={() => setFilterOpen((o) => !o)}
            filterButtonActive={filterOpen || propertyValues.length > 0}
            actions={
              <RelevanceEditToggle
                editing={liveEditing}
                onToggle={handleToggle}
                pending={saving}
                disabled={disabled}
              />
            }
          />
        }
        pagination={
          <TablePaginationAny
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(size: number) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        }
      />
    </div>
  );
}
